import React, { Fragment, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { IonIcon } from "@ionic/react";
import { addOutline, chevronDown } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { City } from "../../../../../components/AddressForm/types";

interface CityComboboxProps {
  value: string;
  invalid?: boolean;
  disabled?: boolean;
  filteredCities: City[];
  onChange: (value: string) => void;
  label: string;
  icon?: string;
  placeholder?: string;
}

export const CityCombobox = ({
  value,
  onChange,
  filteredCities,
  invalid = false,
  disabled = false,
  label,
  icon,
  placeholder,
}: CityComboboxProps) => {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  const hasValue = Boolean(value);

  return (
    <div className="w-full mx-5 mb-2.5">
      <Combobox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <div className="relative w-full ">
            <div
              className={classNames("absolute left-0 top-0  pointer-events-none transition-all duration-200", {
                " top-0": focused || hasValue,
                "top-[2.1875rem]": !focused && !hasValue,
              })}
            >
              <div className="flex items-center gap-1">
                {!focused && !hasValue && <IonIcon icon={addOutline} className="text-[1.3rem]" />}

                <div
                  className={classNames("text-[1.08rem]", {
                    "!text-[0.87rem]": focused || hasValue,
                  })}
                >
                  {label}
                </div>
              </div>
            </div>

            <Combobox.Button className="w-full">
              <Combobox.Input
                className={classNames(
                  "w-full bg-transparent pt-[2.25rem] pb-1 outline-none",
                  disabled && "opacity-50",
                  invalid && "text-red-600",
                )}
                placeholder={placeholder || ""}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => onChange(e.target.value)}
                displayValue={(val: string) => val}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                autoComplete="off"
              />
            </Combobox.Button>

            <Transition
              as={Fragment}
              show={open}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options className="absolute z-50 mt-1 w-[90%] bg-white shadow-lg rounded-xl max-h-60 overflow-y-auto">
                {filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <Combobox.Option
                      key={city.id}
                      value={city.name}
                      className={({ active }) =>
                        classNames("cursor-pointer select-none px-4 py-2", active ? "bg-gray-100" : "")
                      }
                    >
                      {city.name}
                    </Combobox.Option>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-400">{t("Nothing found")}</div>
                )}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
};
